import { NextResponse } from 'next/server';
import type { ApiErrorResponse } from '../interfaces/ApiErrorResponse';

/**
 * @group Functions
 * @summary <blockquote>Handles database errors and returns a standardized error response.</blockquote>
 * @param error - The database error that occurred
 * @param operation - Description of the operation that failed
 * @returns A NextResponse with standardized error structure
 * @example
 * ```typescript
 * try {
 *   const result = await db.query('SELECT * FROM users');
 * } catch (error) {
 *   return handleDatabaseError(error, 'fetching users');
 * }
 * ```
 */
export function handleDatabaseError(error: any, operation: string): NextResponse<ApiErrorResponse> {
  console.error(`Database error during ${operation}:`, error);
  
  let errorMessage = 'Database error';
  let errorDetails = 'An error occurred while accessing the database.';
  let statusCode = 503; // Service Unavailable
  
  // Handle connection timeout errors
  if (error.cause && error.cause.code === 'UND_ERR_CONNECT_TIMEOUT') {
    errorMessage = 'Database connection timeout';
    errorDetails = 'The connection to the database timed out. This might be due to network issues or the database being temporarily unavailable.';
  } 
  // Handle network fetch errors
  else if (error.message && error.message.includes('fetch failed')) {
    errorMessage = 'Network connection error';
    errorDetails = 'Failed to connect to the database due to network issues.';
  }
  // Handle column not found errors
  else if (error.code === '42703') {
    errorMessage = 'Database schema error';
    errorDetails = 'The database query referenced a column that does not exist. This might be due to a migration issue.';
    statusCode = 500; // Internal Server Error
  }
  
  return NextResponse.json({
    error: errorMessage,
    details: errorDetails,
    technical: error.message,
    code: error.cause?.code || error.code
  }, { status: statusCode });
} 